import { useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import type { FarmerProfile } from "@/mocks/farmerProfiles";

export default function FarmerProfileBlock({
  farmer,
  index,
}: {
  farmer: FarmerProfile;
  index: number;
}) {
  const reversed = index % 2 === 1;
  const strip = [
    {
      src: farmer.portrait,
      alt: `Portrait of ${farmer.name}, a ${farmer.crop} farmer from ${farmer.village}`,
    },
    ...farmer.gallery.map((image, photoIndex) => ({
      src: image.src,
      alt:
        (image.alt || "").trim() ||
        `${farmer.crop} work on ${farmer.name}'s farm in ${farmer.village}, photo ${photoIndex + 1}`,
    })),
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const activePhoto = strip[activeIndex] ?? strip[0];

  return (
    <article className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
      <Reveal className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
        <div className="rounded-lg overflow-hidden w-full h-[26rem] md:h-[34rem]">
          <img
            src={activePhoto.src}
            width={1000}
            height={1200}
            alt={activePhoto.alt}
            title={`${farmer.name} - ${farmer.crop} farmer in ${farmer.village}`}
            className="w-full h-full object-cover object-top transition-opacity duration-300"
            decoding="async"
          />
        </div>

        <div className="mt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-3">
            Preview {farmer.name}'s farm
          </p>
          <div className="grid grid-cols-4 gap-1.5">
            {strip.map((photo, photoIndex) => (
              <button
                key={`${farmer.slug}-photo-${photoIndex}`}
                type="button"
                onClick={() => setActiveIndex(photoIndex)}
                aria-label={`Show photo ${photoIndex + 1} of ${strip.length}: ${photo.alt}`}
                className={`h-16 md:h-20 rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
                  activeIndex === photoIndex
                    ? "border-accent-500"
                    : "border-transparent hover:border-accent-300"
                }`}
              >
                <img
                  src={photo.src}
                  width={400}
                  height={400}
                  alt={photo.alt}
                  className="w-full h-full object-cover object-top"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 bg-background-50 border border-background-200 rounded-md px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="font-heading text-lg text-foreground-950 font-semibold">{farmer.name}</p>
            <p className="text-xs text-foreground-600 mt-0.5 flex items-center gap-1.5">
              <i className="ri-map-pin-line text-accent-500"></i>
              {farmer.village}
            </p>
          </div>
          <span className="bg-secondary-100 text-secondary-800 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap">
            {farmer.crop}
          </span>
        </div>
      </Reveal>

      <Reveal className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`} delay={100}>
        <p className="text-xs font-semibold uppercase tracking-wider text-accent-600">
          {farmer.farmingFor}
        </p>
        <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold mt-3">
          {farmer.name}
        </h2>
        <p className="font-heading text-lg italic text-primary-700 mt-3">“{farmer.quote}”</p>

        <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
          {farmer.intro}
        </p>
        <div className="mt-4 space-y-4">
          {farmer.bio.map((paragraph) => (
            <p key={paragraph} className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          {farmer.specialties.map((item) => (
            <span
              key={item}
              className="text-xs font-medium bg-primary-50 text-primary-800 border border-primary-200 px-3 py-1.5 rounded-full"
            >
              {item}
            </span>
          ))}
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to={`/experiences/${farmer.experienceSlug}`}
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Book the {farmer.experienceLabel} experience
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to={`/farmers/${farmer.slug}`}
            className="inline-flex items-center gap-2 bg-background-100 text-foreground-900 border border-background-300 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            Read {farmer.name}'s full profile
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </Reveal>
    </article>
  );
}